import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Form } from '@hologram-dimension/form';
import { SelectField } from '@hologram-dimension/select-field';
import { TextInputField } from '@hologram-dimension/text-input-field';
import useCreateWebhook from 'api/legacy/devices/useCreateWebhook';
import useDeviceWebhook from 'api/legacy/devices/useDeviceWebhook';
import useUpdateWebhook from 'api/legacy/devices/useUpdateWebhook';
import { useCallback, useState, type FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import useOrganization from 'state/contexts/Organization/useOrganization';
import styles from './UpdateWebhookForm.module.css';

interface UpdateWebhookFormProps {
  onDone: () => void;
  deviceId: DeviceId;
}

interface WebhookRHFFormProps {
  port?: number;
  protocol: string;
}

const UpdateWebhookForm: FC<UpdateWebhookFormProps> = ({ deviceId, onDone }) => {
  const [error, setError] = useState<string | null>(null);

  const organizationId = useOrganization();
  const { deviceWebhook, mutate: mutateDeviceWebhook } = useDeviceWebhook(deviceId, organizationId);
  const hasWebhook = deviceWebhook.url && deviceWebhook.url.length > 0;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<WebhookRHFFormProps>({
    defaultValues: {
      port: hasWebhook && deviceWebhook.port ? parseInt(deviceWebhook.port, 10) : undefined,
      protocol: hasWebhook ? deviceWebhook.protocol : 'TCP',
    },
  });

  const createWebhook = useCreateWebhook({ deviceId });
  const updateWebhook = useUpdateWebhook({ deviceId });

  const onGenerateRouterCreds = useCallback(
    async (port: number, protocol: string) => {
      let result;

      try {
        if (hasWebhook) {
          result = await updateWebhook(port, protocol);
        } else {
          result = await createWebhook(port, protocol);
        }
      } catch (generateError) {
        setError(`${generateError}`);
      }

      if (result?.success && result?.data) {
        mutateDeviceWebhook({ data: result.data, success: true }, { revalidate: true });
        onDone();
      }
    },
    [createWebhook, hasWebhook, mutateDeviceWebhook, onDone, updateWebhook]
  );

  const commitWebhookChange = useCallback<SubmitHandler<WebhookRHFFormProps>>(
    async ({ port, protocol }) => {
      if (!port || !protocol) {
        setError('Port and protocol are required');
        return;
      }
      setError(null);
      await onGenerateRouterCreds(port, protocol);
    },
    [onGenerateRouterCreds]
  );

  const onSubmit = handleSubmit(commitWebhookChange);

  return (
    <Form
      className={styles.form}
      onSubmit={onSubmit}
      footerActions={
        <>
          <Button type="submit" loading={isSubmitting}>
            Save
          </Button>
          <Button onClick={onDone} variant="secondary">
            Cancel
          </Button>
        </>
      }
    >
      {error && <BannerNotification variant="error">{error}</BannerNotification>}
      <TextInputField
        label="Port"
        type="number"
        invalid={!!errors.port}
        validationMessage={errors.port?.message}
        {...register('port', {
          valueAsNumber: true,
          min: {
            value: 1,
            message: 'Valid port number is required (1 - 65535)',
          },
          max: { value: 65535, message: 'Valid port number is required (1 - 65535)' },
          required: 'Port is required',
        })}
      />
      <SelectField
        label="Protocol"
        invalid={!!errors.protocol}
        validationMessage={errors.protocol?.message}
        options={[
          { value: 'TCP', label: 'TCP' },
          { value: 'UDP', label: 'UDP' },
        ]}
        {...register('protocol')}
      />
    </Form>
  );
};

export default UpdateWebhookForm;
